import React, { useEffect, useState } from "react";
import api from "../api/api";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Trophy, Package, Clock, Star, Zap, ListChecks, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function periodLabel(p: string): string {
  const [y, m] = p.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function getPrevPeriod(): string {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

function ratingColor(r: string): string {
  if (r === "Exceptional")  return "text-yellow-600 bg-yellow-50";
  if (r === "Excellent")    return "text-green-600 bg-green-50";
  if (r === "Good")         return "text-blue-600 bg-blue-50";
  if (r === "Satisfactory") return "text-gray-600 bg-gray-50";
  if (r === "Needs Improvement") return "text-orange-600 bg-orange-50";
  return "text-red-600 bg-red-50";
}

function ScoreBar({ label, icon, score, max, details }: any) {
  const pct = max > 0 ? (score / max) * 100 : 0;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-gray-800">{label}</span>
          <span className="text-xs text-gray-400">(of {max})</span>
        </div>
        <span className="text-xl font-bold text-gray-900">{score.toFixed(1)}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2 mb-3 overflow-hidden">
        <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, pct)}%` }}/>
      </div>
      {details && <div className="text-xs text-gray-500 space-y-1">{details}</div>}
    </div>
  );
}

export default function MyPerformance() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(getPrevPeriod());
  const [evalData, setEvalData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewedUserName, setViewedUserName] = useState<string>("");
  const [balance, setBalance] = useState<{ annual: any; casual: any } | null>(null);

  const isAdmin = user?.role === "ADMIN";
  const isManager = user?.role === "MANAGER" || user?.role === "DEPARTMENT_HEAD";
  const viewingUserId = searchParams.get("user");
  const isViewingOther = !!viewingUserId && viewingUserId !== user?.id;
  const canViewOthers = isAdmin || isManager;

  const load = () => {
    setLoading(true);
    const endpoint = isViewingOther && canViewOthers
      ? `/performance/employee/${viewingUserId}?period=${period}`
      : `/performance/my?period=${period}`;
    api.get(endpoint)
      .then(r => setEvalData(r.data))
      .catch(() => setEvalData(null))
      .finally(() => setLoading(false));
  };

  // Fetch viewed user name
  useEffect(() => {
    if (isViewingOther && canViewOthers && viewingUserId) {
      api.get(`/users/${viewingUserId}`).then(r => setViewedUserName(r.data?.name || "Employee"))
        .catch(() => setViewedUserName("Employee"));
    } else {
      setViewedUserName("");
    }
  }, [viewingUserId, isViewingOther, canViewOthers]);

  useEffect(() => { load(); }, [period, viewingUserId]);

  useEffect(() => {
    if (isViewingOther) { setBalance(null); return; }
    api.get("/requests/my-balance")
      .then(r => setBalance(r.data))
      .catch(() => setBalance(null));
  }, [isViewingOther]);

  const recalculate = async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    try {
      await api.post("/performance/recalculate", { period });
      load();
    } catch {}
    setRefreshing(false);
  };

  if (loading) return <div className="p-6 text-gray-500">Loading your performance...</div>;
  if (!evalData) return <div className="p-6 text-gray-400">No evaluation data for {periodLabel(period)}</div>;

  const e = evalData;
  const w = e.weights || {};
  const d = e.details || {};

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {isViewingOther && (
            <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 p-1" title="Back">
              <ArrowLeft size={22}/>
            </button>
          )}
          <Trophy className="text-yellow-500" size={28}/>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {isViewingOther ? `${viewedUserName || "Employee"}'s Profile` : "My Profile"}
              {isViewingOther && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-normal">Admin View</span>}
            </h1>
            <p className="text-sm text-gray-500">{periodLabel(period)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input type="month" value={period} onChange={ev => setPeriod(ev.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          {isAdmin && (
            <button onClick={recalculate} disabled={refreshing}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50">
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""}/>
              {refreshing ? "Recalculating..." : "Recalculate"}
            </button>
          )}
        </div>
      </div>

      {/* Final Score Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-6 mb-5 text-center">
        <div className="text-sm text-blue-600 font-medium mb-1">Final Score</div>
        <div className="text-5xl font-bold text-gray-900 mb-2">{e.finalScore.toFixed(1)}<span className="text-2xl text-gray-400">/100</span></div>
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${ratingColor(e.rating)}`}>{e.rating}</span>
        {(e as any).departmentRank && (
          <div className="mt-3 text-xs text-gray-500">Department Rank: #{(e as any).departmentRank}</div>
        )}
      </div>

      {/* Points Breakdown by Source */}
      <div className="space-y-3">
        <ScoreBar
          label="🎧 WHMCS Tickets"
          icon={<Package size={18} className="text-purple-600"/>}
          score={e.outputScore}
          max={Math.max(120, e.finalScore)}
        />

        <ScoreBar
          label="✅ Tasks"
          icon={<Clock size={18} className="text-blue-600"/>}
          score={e.effortScore}
          max={Math.max(120, e.finalScore)}
        />

        <ScoreBar
          label="🗂️ Project Steps"
          icon={<Star size={18} className="text-green-600"/>}
          score={e.qualityScore}
          max={Math.max(120, e.finalScore)}
        />
      </div>

      {/* Leave Balance (self view only) */}
      {!isViewingOther && balance && (
        <div className="mt-6">
          <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            🏖️ Leave Balance
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-blue-700">🏖️ Annual</span>
                <span className="text-xs text-gray-400">of 15</span>
              </div>
              <div className="text-2xl font-bold text-blue-800">
                {balance.annual.available.toFixed(1)}<span className="text-sm text-gray-400"> / {balance.annual.remaining.toFixed(1)}d</span>
              </div>
              {balance.annual.pending > 0 && (
                <div className="text-[11px] text-amber-600 mt-1">{balance.annual.pending.toFixed(1)}d reserved by pending</div>
              )}
            </div>
            <div className="bg-white border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-purple-700">🏝️ Casual</span>
                <span className="text-xs text-gray-400">of 6</span>
              </div>
              <div className="text-2xl font-bold text-purple-800">
                {balance.casual.available.toFixed(1)}<span className="text-sm text-gray-400"> / {balance.casual.remaining.toFixed(1)}d</span>
              </div>
              {balance.casual.pending > 0 && (
                <div className="text-[11px] text-amber-600 mt-1">{balance.casual.pending.toFixed(1)}d reserved by pending</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
